import React from 'react'
import debounce from 'lodash.debounce'
import {
    GoogleMap,
    useJsApiLoader,
    Marker,
    Polygon,
} from '@react-google-maps/api'

// import Grid2 from '@mui/material/Unstable_Grid2' // Grid version 2
import { Box, Stack } from '@mui/material'
import LoadingMessage from './LoadingMessage.js'
import GglPlacesSearch from './GglPlacesSearch.js'

const API_KEY = process.env.REACT_APP_GOOGLE_API_KEY
const libs = ['places']
const containerStyle = {
    width: '100%',
    height: '60vh',
}

const options = {
    disableDefaultUI: true,
    zoomControl: true,
    mapTypeId: 'hybrid',
    clickableIcons: false,
    draggableCursor: 'crosshair',
    styles: [
        {
            featureType: 'poi',
            stylers: [
                {
                    visibility: 'off',
                },
            ],
        },
        {
            featureType: 'transit',
            stylers: [
                {
                    visibility: 'off',
                },
            ],
        },
    ],
}

const polyOptions = {
    strokeColor: '#fbfaca',
    strokeOpacity: 1.0,
    strokeWeight: 2,
    fillColor: '#F7F8C3',
    fillOpacity: 0.2,
    clickable: true,
    draggable: false,
    editable: false,
    visible: true,
}

function BomGoogleMap({
    fencePosts,
    clearAll,
    addFencePost,
    re_positionFencePost,
    setZoomState,
    zoom,
    writeCenterRef,
    center,
}) {
    const { isLoaded, loadError } = useJsApiLoader({
        // a useHook and can't be in useEffect
        id: 'bom-google-map',
        googleMapsApiKey: API_KEY,
        // @ts-ignore
        libraries: libs,
        loading: 'async',
    })

    const mapRef = React.useRef()

    const onMapLoad = React.useCallback((map) => {
        mapRef.current = map
        // console.log('%c Loading the map', 'background: yellow; color: purple')
    }, [])

    const panTo = React.useCallback(
        ({ lat, lng }) => {
            const map = mapRef.current
            if (map) {
                // @ts-ignore
                map.panTo({ lat, lng })
                // @ts-ignore
                setZoomState(17)
            }
            clearAll()
        },
        [clearAll, setZoomState]
    )

    const addFencePostMarker = React.useCallback(
        (event) => {
            // console.log({ lat: event.latLng.lat(), lng: event.latLng.lng() })

            addFencePost(event)
        },
        [addFencePost]
    )

    const onDragEndMarker = (event, selectedIdx) => {
        re_positionFencePost(event, selectedIdx)
    }

    //returns a stable value - a function definition - the memoized value is a function
    const debouncedCenterChangeHandlerMemo = React.useMemo(
        () =>
            debounce(() => {
                if (mapRef.current) {
                    // TODO: 06/20/23 could consider getting the geo center of the fence posts. technique found 'center on marker polygon' in estimator.js in /prod/est_mobile
                    // @ts-ignore
                    writeCenterRef(mapRef.current.getCenter().toJSON())
                    // console.log('center chged')
                }
            }, 200),
        [writeCenterRef]
    )

    const debouncedCenterChangeHandler = React.useCallback(() => {
        debouncedCenterChangeHandlerMemo()
    }, [debouncedCenterChangeHandlerMemo])

    if (loadError) {
        return (
            <LoadingMessage
                msg={
                    'Google Maps cannot be loaded right now. We are very sorry.'
                }
            />
        )
    }
    if (!isLoaded) return <LoadingMessage msg={'Loading Google Maps...'} />

    const mapOptions = { ...options, zoom: zoom }

    return (
        <Stack>
            <Box
                sx={{
                    position: 'relative',
                }}
            >
                <GglPlacesSearch panTo={panTo} />
                <GoogleMap
                    options={mapOptions}
                    // options={options}
                    mapContainerStyle={containerStyle}
                    center={center}
                    onLoad={onMapLoad}
                    // onUnmount={onUnmount}
                    onClick={addFencePostMarker}
                    onZoomChanged={() => {
                        if (mapRef.current) {
                            // @ts-ignore
                            const value = mapRef.current.getZoom()
                            if (!isNaN(value)) {
                                setZoomState(value)
                            }
                        }
                    }}
                    onCenterChanged={debouncedCenterChangeHandler}
                >
                    <>
                        {fencePosts.map((post, idx) => {
                            return (
                                <Marker
                                    key={post.lat}
                                    position={{ lat: post.lat, lng: post.lng }}
                                    draggable={true}
                                    icon={{
                                        url: '/calc/fencePost.png',
                                        anchor: new window.google.maps.Point(
                                            8,
                                            53
                                        ), //{ x: 10, y: 55 },
                                        origin: new window.google.maps.Point(
                                            0,
                                            0
                                        ),
                                        size: new window.google.maps.Size(
                                            60,
                                            60
                                        ),
                                    }}
                                    onDragEnd={(event) =>
                                        onDragEndMarker(event, idx)
                                    }
                                    // onDragEnd={onDragEndMarker}
                                />
                            )
                        })}

                        <Polygon path={fencePosts} options={polyOptions} />
                    </>
                </GoogleMap>
            </Box>
        </Stack>
    )
}

export default BomGoogleMap
